import React, { useState, forwardRef, useImperativeHandle } from "react";
import { Popover } from "@mui/material";
import AxiosInstance from "../../../utils/AxiosInstance";
import DateConverter from "@heartinz/date_and_time_processor";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import { useAuth } from "../../../context/AuthProvider";
import { CalendarIcon, DownArrowAltIcon, TimeIcon, UpArrowAltIcon } from "../../../constants/Icons";

const CashButton = forwardRef(({ trackId, trackMode }, ref) => {
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [results, setResults] = useState([]);
  const { isUser } = useAuth();

  useImperativeHandle(ref, () => ({
    async openPopover(event) {
      setAnchorEl(event.currentTarget);
      setLoading(true);
      try {
        const response = await AxiosInstance.get(
          `/module/boxcash/${trackId}?trackmode=${trackMode}`
        );
        const data = response.data.results || [];
        setResults(data);
      } catch (err) {
        console.error(err);
        setResults([{ error: "API call failed" }]);
      } finally {
        setLoading(false);
      }
    },
    closePopover() {
      setAnchorEl(null);
      setResults([]);
    },
  }));

  const formatCurrency = (value) => {
    const formatted = CurrencyConverter.toCurrency(Math.abs(value), "INR");
    return value < 0 ? `-₹${formatted}` : `₹${formatted}`;
  };

  const getStatusPill = (item) => {
    if (item.cmode === "CASHOUT" || item.amount < 0) {
      return (
        <span className="badge bg-label-danger rounded-pill d-flex align-items-center px-2 py-1">
          <UpArrowAltIcon iClass={"me-1"}/>
          CashOut
        </span>
      );
    }
    if (item.cmode === "CASHIN" || item.amount > 0) {
      return (
        <span className="badge bg-label-success rounded-pill d-flex align-items-center px-2 py-1">
          <DownArrowAltIcon iClass={"me-1"}/>
          CashIn
        </span>
      );
    }
    return (
      <span className="badge bg-secondary rounded-pill px-2 py-1">UNKNOWN</span>
    );
  };

  const open = Boolean(anchorEl);
  const id = open ? "cash-popover" : undefined;

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={() => setAnchorEl(null)}
      anchorOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      PaperProps={{
        sx: { p: 0, minWidth: 240 },
      }}
    >
      <div
        className="popover-content p-2"
        style={{
          backgroundColor: "#fff",
          border: "1px solid #e0e0e0",
          borderRadius: "0.25rem",
        }}
      >
        {/* Close button at top */}
        <div className="d-flex justify-content-between align-items-center">
          <div>Cash</div>
          <i
            className="bx bx-x fs-5"
            style={{ cursor: "pointer" }}
            onClick={() => setAnchorEl(null)}
          />
        </div>

        {/* Border line below close button */}
        <hr className="my-1 mb-1" style={{ borderColor: "#e0e0e0" }} />

        {/* Content container */}
        {/* old p-0 */}
        <div
          className="container-fluid p-1" 
          style={{ maxHeight: "200px", overflowY: "auto" }}
        >
          {results.length === 0 ? (
            <div className="text-center text-muted small py-2">
              {loading ? "Loading..." : "No data found"}
            </div>
          ) : (
            results.map((item, index) => {
              return (
                <div
                  key={index}
                  // old pb-2
                  className="card d-flex flex-column mb-2 p-2"
                  style={{
                    borderBottom:
                      index !== results.length - 1
                        ? "1px solid #e0e0e0"
                        : "none",
                  }}
                >
                  {item.error ? (
                    <div className="text-danger small">{item.error}</div>
                  ) : (
                    <>
                      {/* Amount + CASHIN/CASHOUT */}
                      <div className="d-flex justify-content-between align-items-center">
                        <div
                          style={{
                            fontWeight: 500,
                            fontSize: "0.85rem",
                            color:
                              item.cmode === "CASHOUT" || item.amount < 0
                                ? "#dc3545"
                                : "#28a745",
                          }}
                        >
                          {formatCurrency(item.amount)}
                        </div>
                        {getStatusPill(item)}
                      </div>

                      {/* Date + Time */}
                      <div className="d-flex gap-2 mt-1 small text-secondary">
                        <div className="d-flex align-items-center">
                          <CalendarIcon iClass={"me-1"}/>
                          {
                            DateConverter.ISOToDisplayFormat(
                              DateConverter.convertToTimeZone(
                                item.cashdate,
                                "Asia/Kolkata"
                              )
                            ).date
                          }
                        </div>
                        <div className="d-flex align-items-center">
                          <TimeIcon iClass={"me-1"}/>
                          {
                            DateConverter.ISOToDisplayFormat(
                              DateConverter.convertToTimeZone(
                                item.cashdate,
                                isUser.timezone
                              )
                            ).time
                          }
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </Popover>
  );
});

export default CashButton;
